import { LeftMenuService } from 'app/store/services/left-menu.service';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

// Components
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { RContentComponent } from 'app/dialogs/quick-response/r-content.component';
import { AddAssocProductComponent } from 'app/dialogs/add-assoc-product/add-assoc-product.component';

// Actions
import { viewMessage } from 'app/store/actions/message.action';
import { addAProduct, deleteAProduct, deleteMeeting, refreshMeeting, updateAProduct, updateLeftMenuCount, viewMeeting } from 'app/store/actions/meeting.action';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { loadPipelines } from 'app/store/actions/pipeline.action';

// Reducers
import { EReducers, IReducers } from 'app/store/reducers';

// Models
import { EMeetingStateStatus } from 'app/store/models/meeting.model';
import { EMessageStateStatus, IMessage } from 'app/store/models/message.model';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { EInfoSelectItemType } from 'app/store/models/select.model';
import { DialogData } from 'app/store/models/dialog.model';
import { EPipelineStateStatus, IPipeline, IPipelineState } from 'app/store/models/pipeline.model';

// Services
import { SessionService } from 'app/store/services/session.service';
import { ContactsService } from 'app/store/services/contacts.service';
import { DataSourceParams, ETableColumnType, ITableColumn } from '../../store/models/table.model';
import { IProduct } from '../../store/models/product.model';
import { StaticDataSource } from '../../datasources/static.datasource';
import { MobileService } from '../../store/services/mobile.service';
import { EMenuButtonItemType, IMenuButtonProperties } from '../../store/models/menu-button.model';
import { TableComponent } from '../../shared/ui/table/table.component';
import { SentToCampignDialogComponent } from '../../dialogs/sent-to-campign-dialog/sent-to-campign-dialog.component';
import * as moment from 'moment';
import { EForwardType, ForwardDialogComponent } from '../../dialogs/forward-dialog/forward-dialog.component';
import { DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { DatePipe } from '@angular/common';
import { UtilitiesService } from 'app/store/services/utilities.servive';

@Component({
  selector: 'app-view-meeting',
  templateUrl: './view-meeting.component.html',
  styleUrls: ['./view-meeting.component.scss'],
})
export class ViewMeetingComponent implements OnInit, OnDestroy {
  @Input() messageId: number;

  @ViewChild('tableComponent') tableComponent: TableComponent;

  meetingData: any;
  messages: IMessage[] = [];
  preMessageId: number;
  nextMessageId: number;
  viewAsPlain = false;
  sessionData: IUserSessionData;
  responseButtonSelect: any;
  exportButtonSelect: any;
  contactId: number;
  pipelines: IPipeline[];
  dataReady = false;


  menuItemsProducts: IMenuButtonProperties = {
    items: [
      {
        label: 'Add',
        type: EMenuButtonItemType.BUTTON,
        onClick: () => this.add(),
      },
    ],
  };

  menuItemsTop: IMenuButtonProperties = {
    items: [
      {
        label: 'Back',
        type: EMenuButtonItemType.LINK,
        route: ['/meetings/opportunities'],
      },
      {
        label: 'Edit',
        type: EMenuButtonItemType.BUTTON,
        onClick: () => this.editMeeting(),
      },
      {
        label: 'Delete',
        type: EMenuButtonItemType.BUTTON,
        onClick: () => this.deleteMeeting(),
        class: 'alert-danger',
      },
    ],
  };


  assosiatedProductsTableColumns: ITableColumn<any>[] = [];

  associatedTableReady: boolean;

  associatedDataSource: StaticDataSource<IProduct>;
  associatedDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'id',
    sortDir: 'desc',
    filters: {},
  };

  userTypes = UserType;

  isMobile = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<IReducers>,
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private contactsService: ContactsService,
    private leftMenuService: LeftMenuService,
    private readonly mobileService: MobileService,
    private readonly utilService: UtilitiesService
  ) {
    this.leftMenuService.expand('inbox');
    this.mobileService.observe().pipe(takeUntil(this.destroy$)).subscribe(isMobile => this.isMobile = isMobile);
  }

  ngOnInit() {
    this.exportButtonSelect = {
      buttonProperties: {
        text: 'Export',
        size: ButtonSize.SMALL,
        color: ButtonColor.WHITE,
      },
      items: [
        {
          label: 'Outlook',
          type: EInfoSelectItemType.TYPE_LINK,
          onClick: () =>
            this.contactsService
              .getExportMeeting('vcf', this.route.snapshot.paramMap.get('id'))
              .subscribe((response: Blob) => {
                const dataType = response.type;
                const binaryData = [];
                binaryData.push(response);
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(
                  new Blob(binaryData, { type: dataType }),
                );

                downloadLink.setAttribute(
                  'download',
                  `meeting_${this.route.snapshot.paramMap.get('id')}.vcf`,
                );
                document.body.appendChild(downloadLink);
                downloadLink.click();
              }),
        },
        {
          label: 'CSV Download',
          type: EInfoSelectItemType.TYPE_LINK,
          onClick: () =>
            this.contactsService
              .getExportMeeting('csv', this.route.snapshot.paramMap.get('id'))
              .subscribe((response: Blob) => {
                const dataType = response.type;
                const binaryData = [];
                binaryData.push(response);
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(
                  new Blob(binaryData, { type: dataType }),
                );

                downloadLink.setAttribute(
                  'download',
                  `meeting_${this.route.snapshot.paramMap.get('id')}.csv`,
                );
                document.body.appendChild(downloadLink);
                downloadLink.click();
              }),
        },
      ],
    };

    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.store.dispatch(
            viewMeeting({
              id_meeting: Number(this.route.snapshot.paramMap.get('id')),
            }),
          );
        });

        this.store
          .select(EReducers.MEETING)
          .pipe(takeUntil(this.destroy$))
          .subscribe((state: any) => {
            console.log('here 1', state);
            if (state.state === EMeetingStateStatus.ERROR && state.error) {
              this.store.dispatch(openSnackbar({
                snackbarData: {
                  data: {
                    message: state.error,
                    type: 'error'
                  }
                }
              }));
            }
            if (state.status === EMeetingStateStatus.READY) {
              this.meetingData = state.data;
              this.assosiatedProductsTableColumns = [
                {
                  id: 'name',
                  title: 'Name',
                },
                {
                  id: 'description',
                  title: 'Description',
                  asHtml: true,
                  renderer: (item) => item.description,
                },
                {
                  id: 'est_value',
                  title: 'Estimated Value',
                },
                {
                  id: 'est_close_date',
                  title: 'Estimated closing date',
                },
                {
                  id: 'current_status',
                  title: 'Current status',
                },
                {
                  id: 'pipeline_name',
                  title: 'Pipeline',
                },
                {
                  id: 'status_date',
                  title: 'Status Date',
                  isDateColumn: true,
                },
                {
                  id: 'actual_value',
                  title: 'Actual value',
                },
                {
                  id: 'renewal_date',
                  title: 'Renewal Date',
                  isDateColumn: true,
                },
              ];
              if (this.sessionData.user_type !== UserType.PARTNER && this.sessionData.user_type !== UserType.MANAGER) {
                this.assosiatedProductsTableColumns = [
                  ...this.assosiatedProductsTableColumns,
                  {
                    id: 'update',
                    title: 'Update',
                    type: ETableColumnType.BUTTON,
                    buttonProperties: {
                      color: ButtonColor.ORANGE,
                      size: ButtonSize.SMALL,
                      text: 'Update',
                      onClick: (item: IProduct) => this.update(item),
                    },
                  },
                  {
                    id: 'delete',
                    title: 'Delete',
                    type: ETableColumnType.BUTTON,
                    buttonProperties: {
                      color: ButtonColor.RED,
                      size: ButtonSize.SMALL,
                      text: 'Delete',
                      onClick: (item: IProduct) => this.deleteProduct(item),
                    },
                  },
                ];
              }
              this.associatedDataSource = new StaticDataSource<IProduct>(this.meetingData.products);
              this.associatedTableReady = true;
              this.contactId = state.data.contact_id;
              this.store.dispatch(
                viewMessage({
                  id_message: this.meetingData.message_id,
                }),
              );

              if (!this.messageId) {
                this.messageId = this.meetingData.message_id;
              }

              this.store
                .select(EReducers.MESSAGE)
                .pipe(takeUntil(this.destroy$))
                .subscribe((s: any) => {
                  if (s.status === EMessageStateStatus.READY) {
                    this.messages = s.current_messages;
                    this.preMessageId = s.prev_message_id;
                    this.nextMessageId = s.next_message_id;

                    this.dataReady = true;
                  }
                });

              this.store.dispatch(
                loadPipelines({
                  pagination: {
                    current_page: 1,
                    page_size: 10,
                  },
                }),
              );
              this.store
                .select(EReducers.PIPELINE)
                .pipe(takeUntil(this.destroy$))
                .subscribe((s: IPipelineState) => {
                  if (s.status === EPipelineStateStatus.UPDATED) {
                    this.store.dispatch(
                      loadPipelines({
                        pagination: s.pagination,
                      }),
                    );
                  }

                  if (s.pipelines) {
                    this.pipelines = s.pipelines;
                    if (this.meetingData.products) {
                      this.meetingData.products.forEach(item => {
                        this.pipelines.forEach(p => {
                          if (p.id === item.pipeline) {
                            item.pipename = p.pipeline;
                          }
                        });
                      });
                    }
                  }

                  this.sessionData = sessionData;
                });

              this.responseButtonSelect = {
                buttonProperties: {
                  text: 'Reply',
                  size: ButtonSize.SMALL,
                  color: ButtonColor.ORANGE,
                },
                items: [
                  {
                    label: 'Opportunities',
                    type: EInfoSelectItemType.TYPE_LINK,
                    tag: 'main',
                    dialogContent: this.getDialogData('opportunities'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'main',
                        label: 'Opportunities',
                      }),
                    children: [
                      {
                        label: 'Meetings',
                        tag: 'meeting',
                        type: EInfoSelectItemType.TYPE_LINK,
                        dialogContent: this.getDialogData('meeting'),
                        onClick: () =>
                          this.openItemDialog(this.contactId, {
                            tag: 'meeting',
                            label: 'Meetings',
                            contact_email: this.meetingData.contact_email,
                          }),
                      },
                      {
                        label: 'Order/Quote',
                        tag: 'order',
                        type: EInfoSelectItemType.TYPE_LINK,
                        dialogContent: this.getDialogData('order'),
                        onClick: () =>
                          this.openItemDialog(this.contactId, {
                            tag: 'order',
                            label: 'Order/Quote',
                          }),
                      },
                    ],
                  },
                  {
                    label: 'Cycle',
                    tag: 'cycle',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('cycle'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'cycle',
                        label: 'Cycle',
                      }),
                  },
                  {
                    label: 'Follow up',
                    tag: 'followup',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('follow_up'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'followup',
                        label: 'Follow up',
                        contact_email: this.meetingData.contact_email,
                      }),
                  },
                  {
                    label: 'Inactive',
                    tag: 'inactive',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('inactive'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'inactive',
                        label: 'Inactive',
                        contact_email: this.meetingData.contact_email,
                      }),
                  },
                  {
                    label: 'Remove',
                    tag: 'remove',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('remove'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'remove',
                        label: 'Remove',
                        contact_email: this.meetingData.contact_email,
                      }),
                  },
                  {
                    label: 'Conversation',
                    tag: 'conversation',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('conversation'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'conversation',
                        label: 'Conversation',
                      }),
                  },
                  {
                    label: 'Forward',
                    tag: 'forward',
                    type: EInfoSelectItemType.TYPE_LINK,
                    dialogContent: this.getDialogData('forward'),
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'forward',
                        label: 'Forward',
                      }),
                  },
                  {
                    label: 'Send to campaign',
                    tag: 'send-to-campaign',
                    type: EInfoSelectItemType.TYPE_NORMAL,
                    onClick: () =>
                      this.openItemDialog(this.contactId, {
                        tag: 'send-to-campaign',
                        label: 'Send to campaign',
                      }),
                  }
                  // {
                  //   label: 'Available',
                  //   tag:'available',
                  //   type: EInfoSelectItemType.TYPE_LINK,
                  //   onClick: () => this.openItemDialog(this.contact_id,{tag:'available',label: 'Available',}),
                  // },
                  // {
                  //   label: 'Future',
                  //   tag:'future',
                  //   type: EInfoSelectItemType.TYPE_LINK,
                  //   onClick: () => this.openItemDialog(this.contact_id,{tag:'future',label: 'Future',}),
                  // },
                  // {
                  //   label: 'Not Available',
                  //   tag:'not-available',
                  //   type: EInfoSelectItemType.TYPE_LINK,
                  //   onClick: () => this.openItemDialog(this.contact_id,{tag:'not-available'}),
                  // },
                  // {
                  //   label: 'Send To Campaign',
                  //   tag:'send-to-campaign',
                  //   type: EInfoSelectItemType.TYPE_NORMAL,
                  //   onClick: () => this.openItemDialog(this.contact_id,{tag:'send-to-campaign',label: 'Send To Campaign',}),
                  //   value: 'sent_to_campaign'
                  // },
                ],
              };
            } else {
              this.dataReady = false;
            }
          });
      });
  }

  ngOnDestroy() {
    if (this.destroy$) {
      this.destroy$.next(false);
    }
  }

  getMessageCampaing(): string {
    if (this.messages) {
      for (const message of this.messages) {
        if (message.campaign_name) {
          return message.campaign_name;
        }
      }
    }

    return null;
  }

  openItemDialog(cid, selectedItem) {
    switch (selectedItem.tag) {
      case 'main':
        break;
      case 'got-it':
        this.contactsService
          .quickResponse({ contact_id: cid }, 'okgotit')
          .subscribe(response => {
            this.store.dispatch(updateLeftMenuCount({ data: {} }));
          });
        // v1/messages/okgotit
        break;
      // case 'send-mail':
      //   this.openSendEmailDialog(selectedItem);
      //   break;
      case 'forward':
        this.store.dispatch(openDialog({
          dialogData: {
            title: `Forward ${this.messageId}`,
            canClose: true,
            content: ForwardDialogComponent,
            componentData: {
              messageId: this.messageId,
            },
            buttons: [
              {
                text: 'Forward',
                color: ButtonColor.GREEN,
                size: ButtonSize.NORMAL,
                onClick: (dialog: DialogComponent) => {
                  if (!dialog.dialogForm.valid) {
                    dialog.dialogForm.markAllAsTouched();
                    return;
                  }

                  if (dialog.dialogForm.get('forward_type').value === EForwardType.ACCOUNT) {
                    this.contactsService.forwardResponse(this.messageId, dialog.dialogForm.get('contact_details').value).subscribe(() => {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: 'Message forwarded to your email address',
                                type: 'success',
                                button: {
                                  text: 'dismiss',
                                },
                              },
                              verticalPosition: 'bottom',
                            },
                          }),
                        );
                        dialog.closeDialog();
                      },
                      err => {
                        this.store.dispatch(openSnackbar({
                          snackbarData: {
                            data: {
                              message: err.error,
                              type: 'error',
                            },
                          },
                        }));
                        dialog.closeDialog();
                      },
                    );
                    return;
                  }

                  this.contactsService.forwardResponseToRecipients(this.messageId, dialog.dialogForm.get('recipients').value, dialog.dialogForm.get('contact_details').value)
                    .subscribe(() => {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: `Message forwarded to the following recipients: ${dialog.dialogForm.get('recipients').value}`,
                                type: 'success',
                              },
                            },
                          }),
                        );
                        dialog.closeDialog();
                      },
                      err => {
                        this.store.dispatch(openSnackbar({
                          snackbarData: {
                            data: {
                              message: err.error,
                              type: 'error',
                            },
                          },
                        }));
                        dialog.closeDialog();
                      });
                },
              },
            ],
          },
        }));
        break;
      case 'send-to-campaign':
        this.store.dispatch(openDialog({
          dialogData: {
            content: SentToCampignDialogComponent,
            dialogMinWidth: '600px',
            canClose: true,
            title: `Sent to campaign`,
            buttons: [{
              text: 'Send',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog) => {
                if(!dialog.component.componentReady) {
                  return false;
                }
                if (!dialog.dialogForm.valid) {
                  dialog.dialogForm.markAllAsTouched();
                  return;
                }

                const campaignId = dialog.dialogForm.value.campaign_id;
                const groupId = dialog.dialogForm.value.group_id;
                const startDate = moment(dialog.dialogForm.value.start_date).format('YYYY-MM-DD');

                this.contactsService.sendContactToCampaign(
                  cid,
                  campaignId,
                  groupId,
                  startDate,
                ).subscribe(
                  () => {
                    this.store.dispatch(openSnackbar({
                      snackbarData: {
                        data: {
                          message: 'Contact successfully send to campaign!',
                          type: 'success',
                        },
                      },
                    }));
                    dialog.closeDialog();
                    this.tableComponent.refresh();
                  },
                  err => this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err.error,
                        type: 'error',
                      },
                    },
                  })),
                );
              },
            }],
          },
        }));
        break;
      default:
        this.openDialog(cid, selectedItem);
        break;
    }
  }

  getDialogData(type): DialogData {
    return {
      content: InfoTipsComponent,
      title: 'Tips',
      canClose: true,
      dialogMinWidth: '700px',
      componentData: {
        type,
      },
    };
  }

  openDialog(cid, data) {
    data.contact_email = this.meetingData.contact_email;
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
            contact_id: cid,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          tips: ['cycle', 'followup', 'meeting', 'inactive', 'remove'].indexOf(data.tag) !== -1 ? 'send-update' : null,
        },
      }),
    );
  }

  openSendEmailDialog(data) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          buttons: [
            {
              text: 'Ok',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
            },
            {
              text: 'Close',
              actAsClose: true,
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: () => {
              },
            },
          ],
        },
      }),
    );
  }

  openAddToCampaignDialog(data) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          buttons: [
            {
              text: 'Ok',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
            },
            {
              text: 'Close',
              actAsClose: true,
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: () => {
              },
            },
          ],
        },
      }),
    );
  }

  backToOpp() {
    this.router.navigate(['/meetings/opportunities']);
  }

  editMeeting() {
    this.router.navigate([
      `/messages/edit/meeting/${this.route.snapshot.paramMap.get('id')}`,
    ]);
  }

  deleteMeeting() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          canClose: false,
          content: `Are you sure you want to delete this item ?`,
          title: 'Delete Meeting',
          buttons: [
            {
              actAsClose: true,
              color: ButtonColor.RED,
              text: 'Cancel',
              size: ButtonSize.SMALL,
            },
            {
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.SMALL,
              text: 'Ok',
              onClick: () => {
                this.store.dispatch(
                  deleteMeeting({
                    id_meeting: Number(this.route.snapshot.paramMap.get('id')),
                  }),
                );
                this.router.navigate([`/meetings/opportunities`]);
              },
            },
          ],
        },
      }),
    );
  }

  add() {
    const data = {
      meeting: {
        id: this.route.snapshot.paramMap.get('id'),
      },
    };
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Add new product',
          content: AddAssocProductComponent,
          componentData: {
            data: {
              meeting: {
                id: this.route.snapshot.paramMap.get('id'),
              },
            },
            action: 'new',
          },
          dialogMinWidth: '400px',
          canClose: true,
          buttons: [
            {
              text: 'Create',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog) => this.handleAssociatedProductsDialog('new', dialog, data),
            }
          ]
        },
      }),
    );
  }

  update(item) {
    const data = {
      meeting: {
        id: this.route.snapshot.paramMap.get('id'),
      },
      product: item,  
    };
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Update product',
          content: AddAssocProductComponent,
          componentData: {
            data,
            action: 'update',
          },
          dialogMinWidth: '800px',
          canClose: true,
          buttons: [
            {
              text: 'Update',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog) => this.handleAssociatedProductsDialog('update', dialog, data),
            }
          ]
        },
      }),
    );
  }

  handleAssociatedProductsDialog(action: string, dialog: DialogComponent, inputData: any) {
    if (!dialog.dialogForm.valid) {
      dialog.dialogForm.markAllAsTouched();
      return;
    }
    const form = dialog.dialogForm.getRawValue();
    const pipe = new DatePipe('en-US');
    switch (action) {
      case 'new':
        if (dialog.dialogForm.valid) {
          this.store.dispatch(
            addAProduct({
              meeting: {
                meeting_id: Number(inputData.meeting.id),
              },
              product: form,
            }),
          );
          setTimeout(() => {
          this.store.dispatch(
            refreshMeeting({
              id_meeting: Number(inputData.meeting.id),
            }),
          );
          }, 1000);
        } else {
          dialog.dialogForm.markAllAsTouched();
          return false;
        }
        break;
      case 'update':
        const data = form;

        data.id = inputData.product.id;
        data.status = `${dialog.dialogForm.get('status').value}`;
        data.actual_value = Number(data.actual_value);
        data.estimated_value = Number(data.estimated_value);

        if (moment(data.estimated_date).isValid()) {
          data.estimated_date = pipe.transform(
            data.estimated_date,
            'YYYY-MM-dd',
            this.utilService.getMomentTimezone(this.sessionData.time_zone),
          );
        } else {
          data.estimated_date = null;
        }

        if (moment(data.status_date).isValid()) {
          data.status_date = pipe.transform(
            data.status_date,
            'YYYY-MM-dd',
            this.utilService.getMomentTimezone(this.sessionData.time_zone),
          );
        } else {
          data.status_date = null;
        }

        if (moment(data.renewal_date).isValid()) {
          data.renewal_date = pipe.transform(
            data.renewal_date,
            'YYYY-MM-dd',
            this.utilService.getMomentTimezone(this.sessionData.time_zone),
          );
        } else {
          data.renewal_date = null;
        }

        for (const prop in data) {
          if (!data[prop]) {
            continue;
          }
          if (!data[prop]) {
            delete data[prop];
          }

          if (Array.isArray(data[prop])) {
            const resultArray = data[prop].filter(item => item);
            if (resultArray.length > 0) {
              data[prop] = resultArray;
            } else {
              delete data[prop];
            }
          }
        }

        this.store.dispatch(
          updateAProduct({
            meeting: {
              meeting_id: Number(inputData.meeting.id),
            },
            product: data,
          }),
        );
        break;
    }

    dialog.close();
  }

  deleteProduct(product) {
    this.store.dispatch(
      deleteAProduct({
        meeting: {
          meeting_id: Number(this.route.snapshot.paramMap.get('id')),
        },
        product,
      }),
    );
  }

  onResponseButtonSelection($event: any) {
  }
}
